import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from '../assets/reports.module.css';
import { toast } from 'react-toastify';
import reportsService from '../services/reportsService';

// Import the formatDateForReact function from reportsService
const formatDateForReact = (dateStr) => {
  if (!dateStr) return '';
  
  // If already in React format (yyyy-mm-dd), return as is
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Convert from dd/mm/yyyy to yyyy-mm-dd
  try {
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  } catch (e) {
    console.error("Date parsing error:", e);
    return dateStr; // Return original if parsing fails
  }
};

const Reports = () => {
  const [reportsData, setReportsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRanges, setDateRanges] = useState({});
  const [downloading, setDownloading] = useState({});
  const [showDatePickers, setShowDatePickers] = useState({});

  // Refs for date picker dropdowns
  const datePickerRefs = useRef({});

  // Handle clicks outside date pickers to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(showDatePickers)?.forEach(key => {
        if (showDatePickers[key] && datePickerRefs.current[key] && !datePickerRefs.current[key].contains(event.target)) {
          setShowDatePickers(prev => ({ ...prev, [key]: false }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePickers]);

  // Initialize date ranges for all reports
  useEffect(() => {
    const initialDateRanges = {};
    if (reportsData) {
      reportsData?.sections?.forEach(section => {
        section?.reports?.forEach(report => {
          if (report.requiresDateRange) {
            initialDateRanges[report.route] = {
              dateFrom: '',
              dateTo: ''
            };
          }
        });
      });
      setDateRanges(initialDateRanges);
    }
  }, [reportsData]);

  // Load reports data
  useEffect(() => {
    loadReportsData();
  }, []);

  const loadReportsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to fetch from API first
      try {
        const response = await reportsService.getReportsIndex();
        if (response.success) {
          setReportsData(response.data);
          return;
        }
      } catch (apiError) {
        console.log('API not available, using mock data');
      }

      // Fallback to mock data
      setReportsData(reportsService.getMockReportsData());
    } catch (error) {
      console.error('Error loading reports data:', error);
      setError(error.message);
      
      // Fallback to mock data
      setReportsData(reportsService.getMockReportsData());
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (reportRoute, field, value) => {
    // Store dates in yyyy-mm-dd format internally for the date input
    setDateRanges(prev => ({
      ...prev,
      [reportRoute]: {
        ...prev[reportRoute],
        [field]: value
      }
    }));
  };

  const toggleDatePicker = (reportRoute, field) => {
    const key = `${reportRoute}-${field}`;
    setShowDatePickers(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isDateRangeValid = (reportRoute) => {
    const range = dateRanges[reportRoute];
    return range && range.dateFrom && range.dateTo;
  };

  const handleDownload = async (report, format) => {
    try {
      setDownloading(prev => ({ ...prev, [`${report.route}-${format}`]: true }));

      const requestData = {
        format: format
      };

      // Add date range if required
      if (report.requiresDateRange) {
        const range = dateRanges[report.route];
        if (!range || !range.dateFrom || !range.dateTo) {
          toast.error('Please select both start and end dates');
          return;
        }
        requestData.dateFrom = range.dateFrom;
        requestData.dateTo = range.dateTo;
      }

      // Add additional params if they exist
      if (report.params) {
        Object.assign(requestData, report.params);
      }

      let response;
      let filename;

      // Call appropriate service method based on report type
      switch (report.route) {
        case 'potential-earnings':
          response = await reportsService.getPotentialEarnings(requestData);
          filename = `potential_earnings.${format}`;
          break;
        case 'potential-earnings-vacant':
          response = await reportsService.getPotentialEarningsVacant(requestData);
          filename = `potential_earnings_vacant.${format}`;
          break;
        case 'total-license-fees':
          response = await reportsService.getTotalLicenseFees(requestData);
          const title = requestData.report === 'actual-license-fees' ? 'actual' : 
                       requestData.report === 'license-fees-differences' ? 'outstanding' : 'projected';
          filename = `total_license_fees_${title}.${format}`;
          break;
        case 'petty-cash':
          response = await reportsService.getPettyCash(requestData);
          filename = `petty_cash.${format}`;
          break;
        case 'registered-guardians':
          response = await reportsService.getRegisteredGuardians(requestData);
          filename = `registered_guardians.${format}`;
          break;
        default:
          throw new Error(`Unknown report type: ${report.route}`);
      }

      // Handle file download
      const blob = new Blob([response], { 
        type: format === 'pdf' ? 'application/pdf' : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`${report.name} ${format.toUpperCase()} report downloaded successfully`);
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error(`Failed to download ${format.toUpperCase()} report: ${error.message}`);
    } finally {
      setDownloading(prev => ({ ...prev, [`${report.route}-${format}`]: false }));
    }
  };

  const renderDateInputs = (report) => {
    if (!report.requiresDateRange) {
      return <td></td>;
    }

    const range = dateRanges[report.route] || { dateFrom: '', dateTo: '' };

    return (
      <td className={styles.dateRangeColumn}>
        <div className={styles.dateRangeRow}>
          <div className={styles.dateInputGroup}>
            <div className={styles.dateInput} ref={el => datePickerRefs.current[`${report.route}-dateFrom`] = el}>
              <input
                type="text"
                name="dateFrom"
                className={styles.formControl}
                placeholder="dd/mm/yyyy"
                value={range.dateFrom ? range.dateFrom.replace(/(\d{4})-(\d{2})-(\d{2})/, '$3/$2/$1') : ''}
                readOnly
                onFocus={() => toggleDatePicker(report.route, 'dateFrom')}
              />
              <i 
                className={`${styles.calendarIcon} glyphicon glyphicon-calendar`}
                onClick={() => toggleDatePicker(report.route, 'dateFrom')}
                style={{ cursor: 'pointer' }}
              ></i>
              {showDatePickers[`${report.route}-dateFrom`] && (
                <div className={styles.datePicker}>
                  <input
                    type="date"
                    value={range.dateFrom}
                    onChange={(e) => handleDateChange(report.route, 'dateFrom', e.target.value)}
                    className={styles.datePickerInput}
                    autoFocus
                  />
                </div>
              )}
            </div>
          </div>
          <div className={styles.dateInputGroup}>
            <div className={styles.dateInput} ref={el => datePickerRefs.current[`${report.route}-dateTo`] = el}>
              <input
                type="text"
                name="dateTo"
                className={styles.formControl}
                placeholder="dd/mm/yyyy"
                value={range.dateTo ? range.dateTo.replace(/(\d{4})-(\d{2})-(\d{2})/, '$3/$2/$1') : ''}
                readOnly
                onFocus={() => toggleDatePicker(report.route, 'dateTo')}
              />
              <i 
                className={`${styles.calendarIcon} glyphicon glyphicon-calendar`}
                onClick={() => toggleDatePicker(report.route, 'dateTo')}
                style={{ cursor: 'pointer' }}
              ></i>
              {showDatePickers[`${report.route}-dateTo`] && (
                <div className={styles.datePicker}>
                  <input
                    type="date"
                    value={range.dateTo}
                    onChange={(e) => handleDateChange(report.route, 'dateTo', e.target.value)}
                    className={styles.datePickerInput}
                    autoFocus
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </td>
    );
  };

  const renderDownloadButtons = (report) => {
    if (report.isLink) {
      return (
        <td></td>
      );
    }

    const isDisabled = report.requiresDateRange && !isDateRangeValid(report.route);
    const isDownloadingPDF = downloading[`${report.route}-pdf`];
    const isDownloadingCSV = downloading[`${report.route}-csv`];

    return (
      <td className={styles.downloadColumn}>
        <div className={styles.downloadRow}>
          <button
            type="button"
            className={styles.btn + ' ' + styles.btnSm + ' ' + styles.btnPrimary}
            id={`${report.route}-pdf`}
            disabled={isDisabled || isDownloadingPDF}
            onClick={() => handleDownload(report, 'pdf')}
            title={isDisabled ? "Select date range" : ""}
          >
            {isDownloadingPDF && <span className={styles.loadingSpinner}></span>}
            PDF
          </button>
          <button
            type="button"
            className={styles.btn + ' ' + styles.btnSm + ' ' + styles.btnPrimary}
            id={`${report.route}-csv`}
            disabled={isDisabled || isDownloadingCSV}
            onClick={() => handleDownload(report, 'csv')}
            title={isDisabled ? "Select date range" : ""}
          >
            {isDownloadingCSV && <span className={styles.loadingSpinner}></span>}
            CSV
          </button>
        </div>
      </td>
    );
  };

  if (loading) {
    return (
      <div className={styles.reportsContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.reportsContainer}>
        <div className={styles.errorMessage}>
          Error loading reports: {error}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.reportsContainer} id="js-report-with-daterange">
      {/* Breadcrumb */}
      <nav className={styles.breadcrumb}>
        <Link to="/dashboard" className={styles.breadcrumbItem}>
          Dashboard
        </Link>
        <span> {' > '} </span>
        <span className={styles.breadcrumbActive}>Reports</span>
      </nav>

      {/* Main Panel */}
      <div className={styles.row}>
        <div className={styles.colXs12}>
          <div className={styles.panel + ' ' + styles.panelDefault + ' ' + styles.panelStandard}>
            <div className={styles.panelHeading + ' ' + styles.panelTitle}>
              <h3>Reports</h3>
            </div>
            <div className={styles.panelBody}>
              <div className={styles.row}>
                {reportsData?.sections?.map((section, sectionIndex) => (
                  <div key={sectionIndex} className={styles.colXs12}>
                    <h1 className={styles.marginDown}>{section.title}</h1>
                    
                    {section.title === "Finances" && (
                      <div className={styles.financeSection}>
                        <table className={styles.table + ' ' + styles.tableBordered + ' ' + styles.tableCondensed + ' ' + styles.tableExpenseAccountPaymentSummary}>
                          <thead>
                            <tr>
                              <th>Report</th>
                              <th>Date range</th>
                              <th>Download</th>
                            </tr>
                          </thead>
                          <tbody>
                            {section.reports.map((report, reportIndex) => (
                              <tr key={reportIndex}>
                                <td className={styles.colMd5}>
                                  {report.isLink ? (
                                    <Link to={`/${report.route}`}>
                                      <h4 className={styles.reportName}>{report.name}</h4>
                                    </Link>
                                  ) : (
                                    <h4 className={styles.reportName}>{report.name}</h4>
                                  )}
                                </td>
                                {renderDateInputs(report)}
                                {renderDownloadButtons(report)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {section.title === "Guardians" && (
                      <div className={styles.guardiansSection}>
                        <table className={styles.table + ' ' + styles.tableBordered + ' ' + styles.tableCondensed + ' ' + styles.tableExpenseAccountPaymentSummary}>
                          <thead>
                            <tr>
                              <th>Report</th>
                              <th>Date range</th>
                              <th>Download</th>
                            </tr>
                          </thead>
                          <tbody>
                            {section.reports.map((report, reportIndex) => (
                              <tr key={reportIndex}>
                                <td className={styles.colMd5}>
                                  <h4 className={styles.reportName}>{report.name}</h4>
                                </td>
                                {renderDateInputs(report)}
                                {renderDownloadButtons(report)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {section.title === "Properties" && section.reports.length === 0 && (
                      <div className={styles.emptySection}>
                        No reports available for this section.
                      </div>
                    )}

                    {section.title === "Clients" && section.reports.length === 0 && (
                      <div className={styles.emptySection}>
                        No reports available for this section.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports; 